/**
 * Web twin of WhereHeardPrompt: the same one-question prompt as the shared
 * in-app dialog (WebDialog, which becomes a bottom sheet at phone widths).
 * Shown once per account, right after a CV was opened from My Tabbio; the
 * tiles follow the StepWhereHeard.web recipe so the question reads the same
 * wherever it is asked. One click answers; Skip (footer, X, Escape) is always
 * one click away.
 */
import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { WebDialog } from '../common/WebDialog.web';
import { useWhereHeardPrompt } from '../../hooks/useWhereHeardPrompt';
import { WHERE_HEARD_OPTIONS } from '../onboarding/where-heard-options';

export function WhereHeardPrompt() {
  const prompt = useWhereHeardPrompt();
  const { t } = useTranslation('auth');

  if (!prompt.visible) return null;

  const skipLabel = t('onboarding.skip');

  return (
    <WebDialog
      title={t('onboarding.whereHeard.promptTitle')}
      subtitle={t('onboarding.whereHeard.promptSubtitle')}
      onClose={prompt.skip}
      closeLabel={skipLabel}
      maxWidth={440}
      footer={
        <button
          type="button"
          onClick={prompt.skip}
          disabled={prompt.isSaving}
          style={skipButtonStyle}
        >
          {skipLabel}
        </button>
      }
    >
      <div role="radiogroup" aria-label={t('onboarding.whereHeard.promptTitle')} style={listStyle}>
        {WHERE_HEARD_OPTIONS.map((key) => (
          <OptionTile
            key={key}
            active={prompt.selected === key}
            disabled={prompt.isSaving}
            onSelect={() => void prompt.answer(key)}
            label={t(`onboarding.whereHeard.options.${key}`)}
          />
        ))}
      </div>
    </WebDialog>
  );
}

function OptionTile({
  active,
  disabled,
  onSelect,
  label,
}: {
  active: boolean;
  disabled: boolean;
  onSelect: () => void;
  label: string;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      disabled={disabled}
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...tileStyle,
        backgroundColor: active
          ? 'rgba(0,0,0,0.06)'
          : hovered && !disabled
            ? 'rgba(0,0,0,0.05)'
            : 'rgba(0,0,0,0.04)',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled && !active ? 0.6 : 1,
      }}
    >
      <span style={tileLabelStyle}>{label}</span>
      <MaterialSymbol
        name={active ? 'check_circle' : 'radio_button_unchecked'}
        size={22}
        style={{ color: active ? 'var(--text-primary)' : 'var(--text-tertiary)', flexShrink: 0 }}
      />
    </button>
  );
}

const listStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const tileStyle: CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '14px 18px',
  textAlign: 'start',
  color: 'var(--text-primary)',
  border: 'none',
  borderRadius: 16,
  fontFamily: 'inherit',
  transition: 'background-color 150ms, opacity 150ms',
};

const tileLabelStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 15,
  fontWeight: 500,
};

const skipButtonStyle: CSSProperties = {
  width: '100%',
  height: 44,
  border: 'none',
  borderRadius: 9999,
  backgroundColor: 'transparent',
  color: 'var(--text-secondary)',
  fontSize: 14,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
};
